import { useEffect } from 'react';

import { OrdersModel, refundApi } from '@pukka/api';
import { assertUtil } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { proxy } from '@pukka/valtio';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';

import { _onResetData } from '../Transaction.state';

import {
  Refund,
  RefundItems,
  RefundItemsPromo,
  calculateAmountToRefund,
  constructRefundItemsPromo,
  countPromoRequirement,
  countPromoReward,
  extractForRefundBreakdown,
  getItemAmounts,
  getPromoIdx,
  generateRefundPayload,
} from './refundUtils';

type OrderDetail = OrdersModel.OrderDetail;

type ItemAmounts = Record<string, (number | string)[]>;

interface ITransactionRefundState {
  refundItems: RefundItems;
  refundItemsPromo: RefundItemsPromo;
  transactionDetail: OrderDetail | null;
  showModal: boolean;
  reasonForRefund: string;
  otherReasonForRefund: string;
  isButtonEnabled: boolean;
  amountToRefund: number;
  isSelectedAll: boolean;
  itemAmounts: ItemAmounts;
  isLoading: boolean;
  readonly handleClickSelectAll: () => void;
  readonly handleClickDeselectAll: () => void;
  readonly disableRefund: (salesType: string, idx: number) => void;
  readonly setQtyWithDuplicateReq: (
    promo: Exclude<ReturnType<typeof constructRefundItemsPromo>, undefined>[0],
    idx: number,
    promoIdx: number | null,
    sign: 'plus' | 'minus',
  ) => void;
  readonly handleClickDecreaseQty: (refundItem: Refund) => void;
  readonly handleClickDecreaseQtyPromo: (refundItem: Refund) => void;
  readonly handleClickIncreaseQty: (refundItem: Refund) => void;
  readonly handleClickIncreaseQtyPromo: (refundItem: Refund) => void;
  readonly handleChangeInputAmountRefunded: (refundItem: Refund, value: string | number) => void;
  readonly handleClickCheck: (refundItem: Refund) => void;
  readonly handleClickCheckPromo: (refundItem: Refund) => void;
  readonly handleChangeReason: (value: string) => void;
  readonly handleChangeOtherReason: (value: string) => void;
  readonly handleCloseModal: () => void;
  readonly useTransactionRefund: () => void;
}

const TransactionRefundState = proxy<ITransactionRefundState>({
  refundItems: {},
  refundItemsPromo: [],
  transactionDetail: null,
  showModal: false,
  reasonForRefund: '',
  otherReasonForRefund: '',
  isLoading: false,
  get amountToRefund(): number {
    if (TransactionRefundState.transactionDetail) {
      return calculateAmountToRefund(
        TransactionRefundState.transactionDetail,
        TransactionRefundState.refundItems,
        TransactionRefundState.refundItemsPromo,
      );
    }

    return 0;
  },
  get isButtonEnabled(): boolean {
    // if all items are refunded disable it
    let hasItemRefunded = false;

    // eslint-disable-next-line guard-for-in
    for (const salesType in TransactionRefundState.refundItems) {
      // eslint-disable-next-line no-loop-func
      TransactionRefundState.refundItems[salesType].forEach((refundItem) => {
        if (refundItem.pricePerItem > 0) {
          // item with 0 price can't be refunded, except from promo
          hasItemRefunded = hasItemRefunded || refundItem.isRefunded;
        }
      });
    }

    const reason =
      TransactionRefundState.reasonForRefund === 'Other'
        ? TransactionRefundState.otherReasonForRefund
        : TransactionRefundState.reasonForRefund;

    return hasItemRefunded && !!reason && TransactionRefundState.amountToRefund !== 0;
  },
  get isSelectedAll(): boolean {
    const allRefunds = flatMap(Object.values(TransactionRefundState.refundItems));

    return !allRefunds.some((refundItem) => refundItem.maxQuantity > 0 && !refundItem.isRefunded);
  },
  get itemAmounts(): ItemAmounts {
    return getItemAmounts(TransactionRefundState.refundItems);
  },
  handleClickSelectAll() {
    // eslint-disable-next-line guard-for-in
    for (const salesType in TransactionRefundState.refundItems) {
      TransactionRefundState.refundItems[salesType].forEach((refundItem) => {
        if (refundItem.maxQuantity > 0) {
          // can be refunded if only max_qty > 0
          refundItem.isRefunded = true;
        }
      });
    }

    if (TransactionRefundState.refundItemsPromo && TransactionRefundState.refundItemsPromo.length > 0) {
      TransactionRefundState.refundItemsPromo.forEach((refundItemPromo) => {
        refundItemPromo.isChecked = true;
      });
    }
  },
  handleClickDeselectAll() {
    // eslint-disable-next-line guard-for-in
    for (const salesType in TransactionRefundState.refundItems) {
      TransactionRefundState.refundItems[salesType].forEach((refundItem) => {
        refundItem.isRefunded = false;
      });
    }

    if (TransactionRefundState.refundItemsPromo && TransactionRefundState.refundItemsPromo.length > 0) {
      TransactionRefundState.refundItemsPromo.forEach((promo) => {
        promo.isChecked = false;
      });
    }
  },
  disableRefund(salesType: string, idx: number) {
    TransactionRefundState.refundItems[salesType][idx].isRefunded = false;
  },
  // to handle https://mokapos.atlassian.net/browse/FD-2795
  setQtyWithDuplicateReq(
    promo: Exclude<ReturnType<typeof constructRefundItemsPromo>, undefined>[0],
    idx: number,
    promoIdx: number | null,
    sign: 'plus' | 'minus',
  ) {
    const temp: RefundItemsPromo[0]['items'] = [];
    const arrRefundItems: Refund[] = [];
    let totalQty = 0;

    // if items in one bundle
    if (idx === promoIdx) {
      if (promo.items && promo.items.length > 0) {
        // push items promo with same requirement in temp
        promo.items.forEach((item) => {
          temp.push(item);
        });

        // total quantity of items with same requirement
        temp.forEach((item) => {
          totalQty += item.amount ?? 0;
        });

        // eslint-disable-next-line guard-for-in
        for (const salesType in TransactionRefundState.refundItems) {
          TransactionRefundState.refundItems[salesType].forEach((item) => {
            if (temp.length > 0) {
              // find items in refundItems (refund_availibility)
              if (temp.find((a) => a.checkout_uuid === item.parentCheckoutUuid)) {
                arrRefundItems.push(item);
              }
            }
          });
        }
      }
    }

    // do decrease from last index. increase from first index

    const counter = parseInt((totalQty / promo.count) as unknown as string, 10);
    let totalRefundedQty = 0;

    arrRefundItems.forEach((item) => {
      totalRefundedQty += item.maxQuantity ?? 0;
    });

    // if clicked button is "-" button
    if (sign === 'minus') {
      let index = 0;

      arrRefundItems.reverse();

      while (index < arrRefundItems.length) {
        if (index !== arrRefundItems.length - 1) {
          if (arrRefundItems[index].refundAmount > 0 && counter < totalRefundedQty) {
            arrRefundItems[index].refundAmount -= counter;
            break;
          }
        } else if (arrRefundItems[index].refundAmount > counter) {
          arrRefundItems[index].refundAmount -= counter;
          break;
        }

        index += 1;
      }
    } else {
      // if clicked button is "+" button
      let index = 0;

      while (index < arrRefundItems.length) {
        if (arrRefundItems[index].refundAmount < arrRefundItems[index].maxQuantity) {
          arrRefundItems[index].refundAmount += counter;
          break;
        }

        index += 1;
      }
    }
  },
  handleClickDecreaseQty(refundItem: Refund) {
    if (refundItem.refundAmount > 1) {
      refundItem.refundAmount -= 1;
    }
  },
  handleClickDecreaseQtyPromo(refundItem: Refund) {
    const promoIdx = getPromoIdx(refundItem, TransactionRefundState.refundItemsPromo);

    TransactionRefundState.refundItemsPromo.forEach((promo, idx) => {
      // get all promo requirement id in a bundle promo
      const valueArr = promo.items?.map((x) => x.promo_requirement_id);
      // check if items in a bundle promo has duplicate requirement id
      const isDuplicate = valueArr?.some((val, i) => valueArr.indexOf(val) !== i);

      if (!isDuplicate) {
        if (idx === promoIdx) {
          if (promo.items && promo.items.length > 0) {
            promo.items.forEach((item) => {
              Object.keys(TransactionRefundState.refundItems).forEach((salesType) => {
                TransactionRefundState.refundItems[salesType].forEach((refundItemForSalesType) => {
                  if (refundItemForSalesType.parentCheckoutUuid === item.order_item_uuid) {
                    const counter =
                      item.promo_item_status !== 'REWARD'
                        ? countPromoRequirement(item.promo_requirement_id, TransactionRefundState.refundItemsPromo)
                        : countPromoReward(item.promo_reward_id, TransactionRefundState.refundItemsPromo);

                    const minAmountPromo = parseInt(counter as unknown as string, 10);

                    if (refundItemForSalesType.refundAmount > minAmountPromo) {
                      refundItemForSalesType.refundAmount -= minAmountPromo;
                    }
                  }
                });
              });
            });
          }
        }
      } else {
        TransactionRefundState.setQtyWithDuplicateReq(promo, idx, promoIdx, 'minus');
      }
    });
  },
  handleClickIncreaseQty(refundItem: Refund) {
    if (refundItem.refundAmount < refundItem.maxQuantity) {
      refundItem.refundAmount += 1;
    }
  },
  handleClickIncreaseQtyPromo(refundItem: Refund) {
    const promoIdx = getPromoIdx(refundItem, TransactionRefundState.refundItemsPromo);

    TransactionRefundState.refundItemsPromo?.forEach((promo, idx) => {
      // get all promo requirement id in a bundle promo
      const valueArr = promo.items?.map((x) => x.promo_requirement_id);
      // check if a bundle promo has duplicate requirement id
      const isDuplicate = valueArr?.some((val, i) => valueArr.indexOf(val) !== i);

      if (!isDuplicate) {
        if (idx === promoIdx) {
          if (promo.items && promo.items.length > 0) {
            promo.items.forEach((item) => {
              Object.keys(TransactionRefundState.refundItems).forEach((salesType) => {
                TransactionRefundState.refundItems[salesType].forEach((refundItemForSalesType) => {
                  if (refundItemForSalesType.parentCheckoutUuid === item.order_item_uuid) {
                    const counter =
                      item.promo_item_status !== 'REWARD'
                        ? countPromoRequirement(item.promo_requirement_id, TransactionRefundState.refundItemsPromo)
                        : countPromoReward(item.promo_reward_id, TransactionRefundState.refundItemsPromo);

                    if (refundItemForSalesType.refundAmount < refundItemForSalesType.maxQuantity) {
                      refundItemForSalesType.refundAmount += parseInt(counter as unknown as string, 10);
                    }
                  }
                });
              });
            });
          }
        }
      } else {
        TransactionRefundState.setQtyWithDuplicateReq(promo, idx, promoIdx, 'plus');
      }
    });
  },
  handleChangeInputAmountRefunded(refundItem: Refund, value: string | number) {
    const { maxQuantity } = refundItem;

    refundItem.refundAmount = Math.min(parseInt(value as string, 10) || maxQuantity, maxQuantity);
  },
  handleClickCheck(refundItem: Refund) {
    refundItem.isRefunded = !refundItem.isRefunded;
  },
  handleClickCheckPromo(refundItem: Refund) {
    const promoIdx = getPromoIdx(refundItem, TransactionRefundState.refundItemsPromo);

    TransactionRefundState.refundItemsPromo?.forEach((promo, idx) => {
      if (idx === promoIdx) {
        promo.isChecked = !promo.isChecked;

        // refund an item of promo should refund all items of selected package promo
        if (promo.items && promo.items.length > 0) {
          promo.items.forEach((itemPromo) => {
            // eslint-disable-next-line guard-for-in
            for (const salesType in TransactionRefundState.refundItems) {
              TransactionRefundState.refundItems[salesType].forEach((item) => {
                if (item.parentCheckoutUuid === itemPromo.order_item_uuid) {
                  item.isRefunded = promo.isChecked;
                }
              });
            }
          });
        }
      }
    });
  },
  handleChangeReason(value: string) {
    TransactionRefundState.reasonForRefund = value;
  },
  handleChangeOtherReason(value: string) {
    TransactionRefundState.otherReasonForRefund = value;
  },
  handleCloseModal() {
    TransactionRefundState.showModal = false;
    TransactionRefundState.refundItems = {};
    TransactionRefundState.refundItemsPromo = [];
    TransactionRefundState.reasonForRefund = '';
    TransactionRefundState.otherReasonForRefund = '';
  },
  useTransactionRefund() {
    useEffect(() => {
      if (
        TransactionRefundState.showModal &&
        TransactionRefundState.transactionDetail &&
        isEmpty(TransactionRefundState.refundItems) &&
        isEmpty(TransactionRefundState.refundItemsPromo)
      ) {
        const refundItems = extractForRefundBreakdown(TransactionRefundState.transactionDetail);

        TransactionRefundState.refundItems = refundItems;
        TransactionRefundState.refundItemsPromo = constructRefundItemsPromo(TransactionRefundState.transactionDetail, refundItems);
      }
    }, []);
  },
});

function showRefundModal() {
  TransactionRefundState.showModal = true;
}

function handleClickMinus(isPromoItem: boolean, refundItem: Refund) {
  if (!isPromoItem) {
    TransactionRefundState.handleClickDecreaseQty?.(refundItem);
  } else {
    TransactionRefundState.handleClickDecreaseQtyPromo?.(refundItem);
  }
}

function handleClickPlus(isPromoItem: boolean, refundItem: Refund) {
  if (!isPromoItem) {
    TransactionRefundState.handleClickIncreaseQty?.(refundItem);
  } else {
    TransactionRefundState.handleClickIncreaseQtyPromo?.(refundItem);
  }
}

function handleCheckboxChange(isPromoItem: boolean, refundItem: Refund) {
  if (!isPromoItem) {
    TransactionRefundState.handleClickCheck?.(refundItem);
  } else {
    TransactionRefundState.handleClickCheckPromo?.(refundItem);
  }
}

function handleInputChange(isPromoItem: boolean, refundItem: Refund, value: string | number) {
  if (!isPromoItem) {
    const inputNumber = Number(value);

    if (!Number.isNaN(inputNumber)) {
      if (inputNumber > 0) {
        TransactionRefundState.handleChangeInputAmountRefunded?.(refundItem, inputNumber);
      }
    }
  }
}

async function handleIssueRefund(onCloseDetail?: () => void, refundOrder = refundApi.refundOrder, State = TransactionRefundState) {
  // disable button issue refund to prevent multiple click by user
  State.isLoading = true;

  const payload = generateRefundPayload(State);

  if (payload) {
    try {
      await refundOrder(payload);

      // add delay because sometimes the list from BE needs time to be updated
      setTimeout(() => {
        State.isLoading = false;
        State.handleCloseModal();
        onCloseDetail?.();
        _onResetData('success');
      }, 2000);
    } catch (error) {
      assertUtil.assertObjectIsTruthy<{
        message: string;
      }>(error);

      State.isLoading = false;

      toast.error(error.message);
    }
  } else {
    State.isLoading = false;
  }
}

export type { ITransactionRefundState, ItemAmounts };
export {
  TransactionRefundState,
  showRefundModal,
  handleClickMinus,
  handleClickPlus,
  handleCheckboxChange,
  handleInputChange,
  handleIssueRefund,
};
